import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, Modal } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { FilterList } from '@mui/icons-material';
import { TroncFiltersContext } from '../../../context/TroncFiltersContext';
import TroncReportFiltersPanel from './TroncReportFiltersPanel';
import { UIContext } from '../../../context/UIContext';
import { defer } from '../../../helpers/helpers';
import FailedToLoadTronc from '../../../components/FailedToLoad';
import Loading from '../../../components/Loading';
import { TroncRow, TroncSummary } from '../../../models/tronc';
import { setupArrayAtPolyfill } from '../../../polyfills';
import EmptyResult from '../../../components/EmptyResult';
import TroncMobileContainer from './TroncMobileContainer';
import { TroncMobileToolbar } from './TroncMobileToolbar';
import { ExportModal } from './ExportModal';
import EmptyResultMobile from '../../../components/EmptyResultMobile';

interface TroncTableMobileProps {
  rows: TroncRow[];
  summary: TroncSummary;
  currency: string;
  isLoading: boolean;
  isError: boolean;
  refresh: () => void;
}

const TroncTableMobile = (props: TroncTableMobileProps) => {
  const { rows, summary, currency, isLoading, isError, refresh
 } = props;
  const { t } = useTranslation();
  const [exportReportStarted, setExportReportStarted] = useState(false);  const { filtersOpen, openFilters, closeFilters, chips, isLoading: isLoadingFiltersContext } = useContext(TroncFiltersContext);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
  const { colorMode } = useContext(UIContext);
  const [filterColor, setFilterColor] = useState<"greyed" | undefined>('greyed');
  const [exportColor, setExportColor] = useState<"greyed" | undefined>('greyed');

  // Support old browsers
  useEffect(setupArrayAtPolyfill, []);

  const toggleFilters = useCallback(() =>
    defer(() => {
      if (filtersOpen) {
        closeFilters(true);
      } else {
        openFilters();
      }
    }), [filtersOpen, closeFilters, openFilters]
  );


  const toolbarChips = useMemo(() =>
    chips.map((chip) => ({
      ...chip,
      onClick: chip.onClick ? chip.onClick : toggleFilters,
    })), [chips, toggleFilters]
  );

  const exportOnComplete = useCallback(() => {
    setExportReportStarted(false);
  }, [setExportReportStarted]
  );

  const exportFilteredTronc = useCallback(() => {
    setExportModalOpen(true);
    if (exportReportStarted) {
      return;
    }
    setExportReportStarted(true);
  }, [exportReportStarted, setExportReportStarted, setExportModalOpen]
  );

  const resetButtons = useCallback(() => {
    setExportColor('greyed');
    setFilterColor('greyed');
  }, [setFilterColor, setExportColor]);

  const toggleModal = () => {
    setModalOpen(currentModalOpen => !currentModalOpen);
    resetButtons();
  }
  const handleCloseModal = (event: unknown, reason: string) => {
    if (reason && reason === "backdropClick") setModalOpen(false); 
  }

  const handleCloseExportModal = (event: unknown, reason: string) => {
    if (reason && reason === "backdropClick") {
      setExportModalOpen(false); 
    }
  }

  const openFiltersModal = useCallback(() => {
    setFilterColor(undefined);
    openFilters();
  }, [openFilters, setFilterColor]);

  const openExportModal = useCallback(() => {
    setExportColor(undefined);
    exportFilteredTronc();
  }, [setExportColor, exportFilteredTronc])

  const getExportRows = () => (
    [...rows,
    {
      requestStartTime: '',
      requestEndTime: '',
      staffName: '',
      staffId: '',
      locationId: '',
      locationName: 'Total',
      hours: summary.totalHours,
      gratuity: summary.totalGratuity,
      serviceCharge: summary.totalServiceCharge,
      totalTronc: summary.totalTronc,
      noOfShiftRecords: 0
    }]
  );

  return (
      <Box sx={{ width: '100%' }}>
        {!isLoadingFiltersContext && <TroncMobileToolbar
          onFilterClick={toggleModal}
          chips={toolbarChips}
          isFilterOpen={filtersOpen}
        />}
        {!isLoading && isError && (
            <FailedToLoadTronc  onClickRetry={() => defer(refresh)} />
        )}
        {rows.length === 0 && !isLoading && !isError && <EmptyResultMobile title={t('components.iconMessage.noTronc.title')} subtitle={t('components.iconMessage.noTronc.subtitle')} />}
        {isLoading && <Loading />}
        {!isLoading && !isError && rows.length > 0 &&  <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }} >
            <TroncMobileContainer
              rows={rows}
              currency={currency}
            /> 
        </Box>}

        <Dialog open={filtersOpen} PaperProps={{ sx: { borderRadius: 0 } }} fullScreen>
          <TroncReportFiltersPanel />
        </Dialog>
        <Modal
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          open={modalOpen}
          onClose={handleCloseModal}
        >
          <Box sx={{
            backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
            flexDirection: `column`, borderRadius: '8px', padding: '8px'
          }}>
            <Button onClick={openFiltersModal} sx={{ width: '100%' }} color={filterColor}>
              <Box sx={{ marginRight: 'auto', display: 'flex', flexDirection: 'row' }}>
                <FilterList sx={{ marginRight: '8px' }} />
                {t('components.filters.filters')}
              </Box>
            </Button>
            {!isError && !isLoading && rows.length > 0 && (
              <Button
                aria-haspopup="true"
                sx={{ width: '100%' }}
                onClick={openExportModal}
                variant="text"
                color={exportColor}
              >
                <Box sx={{ marginRight: 'auto', display: 'flex', flexDirection: 'row' }}>
                  <FileDownloadOutlinedIcon sx={{ marginRight: '8px' }} />
                  {t('actions.export')}
                </Box>
              </Button>
            )}
          </Box>
        </Modal>
        <Modal
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          open={exportModalOpen}
          onClose={handleCloseExportModal}
        >
          <ExportModal
            start={exportReportStarted}
            onComplete={exportOnComplete}
            rows={getExportRows()}
            closeAction={() => setExportModalOpen(false)}
          />
        </Modal>
        
      </Box>
  )
}


export default TroncTableMobile;
